<script setup lang="ts">
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import FieldSet from 'primevue/fieldset';
import { ColorPicker, InputGroup, InputGroupAddon } from 'primevue';
import { computed, ref } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import Message from 'primevue/message';
import ProblemDetailsView from 'shared/problems/components/ProblemDetailsView.vue';
import type { ProblemDetails } from "shared/problems/domain/problemDetails";
import FormulaEditor from '@/common/components/FormulaEditor.vue';
import { useUpdateMapStyle, type UpdateMapStyleRequest } from '../api/updateMapStyle';
import { useCreateMapStyle, type CreateMapStyleRequest } from '../api/createMapStyle';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import Splitter from 'primevue/splitter';
import SplitterPanel from 'primevue/splitterpanel';
import PreviewMapView from './PreviewMapView.vue';
import { createMapStyle, type CreateMapStyleResult } from 'shared/mapStyles/domain/createMapStyle';
import type { CompilationResult } from '@datapolarna/formulaparser';
import { language as shapeLanguage } from 'shared/mapStyles/domain/shapeFormulaLanguage';
import { language as vehicleLanguage } from 'shared/mapStyles/domain/vehicleFormulaLanguage';
import { language as placeLanguage } from 'shared/mapStyles/domain/placeFormulaLanguage';
import { language as packageLanguage } from 'shared/mapStyles/domain/packageFormulaLanguage';
import { language as proximityLanguage } from 'shared/mapStyles/domain/proximityFormulaLanguage';
import type { MapStyleResponse } from 'shared/mapStyles/api/mapStyleResponse';

defineExpose({
  show
});

const styleId = ref<string>();

const createMapStyleApi = useCreateMapStyle();
const updateMapStyle = useUpdateMapStyle();

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const dialogTranslations = translations.value.mapStyles.editDialog;

const visible = ref<boolean>();

const loading = ref(false);
const problems = ref<ProblemDetails>();

const initialValues = ref<ShowDialog>();

const title = ref('');

const name = ref('');

const color = ref<string>('4CAF50');

const nameChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.name !== name.value;
});

const nameInvalid = computed(() => {
  if (name.value === '') {
    return translations.value.error.nameIsRequired;
  }

  return undefined;
})

const shapeLabelFormula = ref('');

const shapeLabelFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.shapeLabelFormula !== shapeLabelFormula.value;
});

const shapeColorFormula = ref('');

const shapeColorFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.shapeColorFormula !== shapeColorFormula.value;
});

const shapeOutlineColorFormula = ref('');

const shapeOutlineColorFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.shapeOutlineColorFormula !== shapeOutlineColorFormula.value;
});

const shapeOutlineSizeFormula = ref('');

const shapeOutlineSizeFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.shapeOutlineSizeFormula !== shapeOutlineSizeFormula.value;
});

const vehicleColorFormula = ref('');

const vehicleColorFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.vehicleColorFormula !== vehicleColorFormula.value;
});

const shapeFontSizeFormula = ref('');

const shapeFontSizeFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.shapeFontSizeFormula !== shapeFontSizeFormula.value;
});

const shapeMessageIconFormula = ref('');

const shapeMessageIconFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.shapeMessageIconFormula !== shapeMessageIconFormula.value;
});

// Persisted (per-user) expanded/collapsed state of the storage places section.
const placesSectionCollapsed = useLocalStorage('map-style-places-section-collapsed', false);

const placeVisibleFormula = ref('');
const placeVisibleFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.placeVisibleFormula !== placeVisibleFormula.value;
});

const placeLabelFormula = ref('');
const placeLabelFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.placeLabelFormula !== placeLabelFormula.value;
});

const placeOutlineColorFormula = ref('');
const placeOutlineColorFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.placeOutlineColorFormula !== placeOutlineColorFormula.value;
});

const placeOutlineSizeFormula = ref('');
const placeOutlineSizeFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.placeOutlineSizeFormula !== placeOutlineSizeFormula.value;
});

const placeAccessibleOutlineColorFormula = ref('');
const placeAccessibleOutlineColorFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.placeAccessibleOutlineColorFormula !== placeAccessibleOutlineColorFormula.value;
});

const placeInaccessibleOutlineColorFormula = ref('');
const placeInaccessibleOutlineColorFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.placeInaccessibleOutlineColorFormula !== placeInaccessibleOutlineColorFormula.value;
});

const placeTextAlignmentFormula = ref('');
const placeTextAlignmentFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.placeTextAlignmentFormula !== placeTextAlignmentFormula.value;
});

const placeTextPositionFormula = ref('');
const placeTextPositionFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.placeTextPositionFormula !== placeTextPositionFormula.value;
});

const placeTextOpacityFormula = ref('');
const placeTextOpacityFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.placeTextOpacityFormula !== placeTextOpacityFormula.value;
});

const placeTextColorFormula = ref('');
const placeTextColorFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.placeTextColorFormula !== placeTextColorFormula.value;
});

const placeTextSizeFormula = ref('');
const placeTextSizeFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.placeTextSizeFormula !== placeTextSizeFormula.value;
});

const placeBackgroundColorFormula = ref('');
const placeBackgroundColorFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.placeBackgroundColorFormula !== placeBackgroundColorFormula.value;
});

const placeBackgroundPatternFormula = ref('');
const placeBackgroundPatternFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.placeBackgroundPatternFormula !== placeBackgroundPatternFormula.value;
});

const placeBackgroundOpacityFormula = ref('');
const placeBackgroundOpacityFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.placeBackgroundOpacityFormula !== placeBackgroundOpacityFormula.value;
});

const packageVisibleFormula = ref('');
const packageVisibleFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.packageVisibleFormula !== packageVisibleFormula.value;
});

const packageLabelFormula = ref('');
const packageLabelFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.packageLabelFormula !== packageLabelFormula.value;
});

const packageOutlineColorFormula = ref('');
const packageOutlineColorFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.packageOutlineColorFormula !== packageOutlineColorFormula.value;
});

const packageOutlineSizeFormula = ref('');
const packageOutlineSizeFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.packageOutlineSizeFormula !== packageOutlineSizeFormula.value;
});

const packageTextAlignmentFormula = ref('');
const packageTextAlignmentFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.packageTextAlignmentFormula !== packageTextAlignmentFormula.value;
});

const packageTextPositionFormula = ref('');
const packageTextPositionFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.packageTextPositionFormula !== packageTextPositionFormula.value;
});

const packageTextOpacityFormula = ref('');
const packageTextOpacityFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.packageTextOpacityFormula !== packageTextOpacityFormula.value;
});

const packageTextColorFormula = ref('');
const packageTextColorFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.packageTextColorFormula !== packageTextColorFormula.value;
});

const packageTextSizeFormula = ref('');
const packageTextSizeFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.packageTextSizeFormula !== packageTextSizeFormula.value;
});

const packageBackgroundColorFormula = ref('');
const packageBackgroundColorFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.packageBackgroundColorFormula !== packageBackgroundColorFormula.value;
});

const packageBackgroundPatternFormula = ref('');
const packageBackgroundPatternFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.packageBackgroundPatternFormula !== packageBackgroundPatternFormula.value;
});

const packageBackgroundOpacityFormula = ref('');
const packageBackgroundOpacityFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.packageBackgroundOpacityFormula !== packageBackgroundOpacityFormula.value;
});

const proximityVisibleFormula = ref('');
const proximityVisibleFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.proximityVisibleFormula !== proximityVisibleFormula.value;
});

const proximityAreaColorFormula = ref('');
const proximityAreaColorFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.proximityAreaColorFormula !== proximityAreaColorFormula.value;
});

const proximityConeColorFormula = ref('');
const proximityConeColorFormulaChanged = computed(() => {
  if (initialValues.value === undefined) return false;
  return initialValues.value.proximityConeColorFormula !== proximityConeColorFormula.value;
});



const previewMapStyle = computed<CreateMapStyleResult>(() => {
  const previewResponse: MapStyleResponse = {
    id: crypto.randomUUID(),
    name: 'Preview',
    shapeColorFormula: shapeColorFormula.value,
    shapeLabelFormula: shapeLabelFormula.value,
    shapeOutlineColorFormula: shapeOutlineColorFormula.value,
    shapeOutlineSizeFormula: shapeOutlineSizeFormula.value,
    shapeFontSizeFormula: shapeFontSizeFormula.value,
    shapeMessageIconFormula: shapeMessageIconFormula.value,
    vehicleColorFormula: vehicleColorFormula.value,
    placeVisibleFormula: placeVisibleFormula.value,
    placeLabelFormula: placeLabelFormula.value,
    placeOutlineColorFormula: placeOutlineColorFormula.value,
    placeOutlineSizeFormula: placeOutlineSizeFormula.value,
    placeAccessibleOutlineColorFormula: placeAccessibleOutlineColorFormula.value,
    placeInaccessibleOutlineColorFormula: placeInaccessibleOutlineColorFormula.value,
    placeTextAlignmentFormula: placeTextAlignmentFormula.value,
    placeTextPositionFormula: placeTextPositionFormula.value,
    placeTextOpacityFormula: placeTextOpacityFormula.value,
    placeTextColorFormula: placeTextColorFormula.value,
    placeTextSizeFormula: placeTextSizeFormula.value,
    placeBackgroundColorFormula: placeBackgroundColorFormula.value,
    placeBackgroundPatternFormula: placeBackgroundPatternFormula.value,
    placeBackgroundOpacityFormula: placeBackgroundOpacityFormula.value,
    packageVisibleFormula: packageVisibleFormula.value,
    packageLabelFormula: packageLabelFormula.value,
    packageOutlineColorFormula: packageOutlineColorFormula.value,
    packageOutlineSizeFormula: packageOutlineSizeFormula.value,
    packageTextAlignmentFormula: packageTextAlignmentFormula.value,
    packageTextPositionFormula: packageTextPositionFormula.value,
    packageTextOpacityFormula: packageTextOpacityFormula.value,
    packageTextColorFormula: packageTextColorFormula.value,
    packageTextSizeFormula: packageTextSizeFormula.value,
    packageBackgroundColorFormula: packageBackgroundColorFormula.value,
    packageBackgroundPatternFormula: packageBackgroundPatternFormula.value,
    packageBackgroundOpacityFormula: packageBackgroundOpacityFormula.value,
    proximityVisibleFormula: proximityVisibleFormula.value,
    proximityAreaColorFormula: proximityAreaColorFormula.value,
    proximityConeColorFormula: proximityConeColorFormula.value,
  };

  return createMapStyle({
    style: previewResponse
  });
});

const shapeLabelCompilationMessage = computed(() => compilationMessage(previewMapStyle.value.compilation.shapeLabel));
const shapeColorCompilationMessage = computed(() => compilationMessage(previewMapStyle.value.compilation.shapeColor));
const shapeOutlineColorCompilationMessage = computed(() => compilationMessage(previewMapStyle.value.compilation.shapeOutlineColor));
const shapeOutlineSizeCompilationMessage = computed(() => compilationMessage(previewMapStyle.value.compilation.shapeOutlineSize));
const shapeFontSizeCompilationMessage = computed(() => compilationMessage(previewMapStyle.value.compilation.shapeFontSize));
const shapeMessageIconCompilationMessage = computed(() => compilationMessage(previewMapStyle.value.compilation.shapeMessageIcon));
const vehicleColorCompilationMessage = computed(() => compilationMessage(previewMapStyle.value.compilation.vehicleColor));

interface ShowDialog {
  id?: string;
  name?: string | null;
  shapeLabelFormula?: string | null;
  shapeColorFormula?: string | null;
  shapeOutlineColorFormula?: string | null;
  shapeOutlineSizeFormula?: string | null;
  vehicleColorFormula?: string | null;
  shapeFontSizeFormula?: string | null;
  shapeMessageIconFormula?: string | null;
  placeVisibleFormula?: string | null;
  placeLabelFormula?: string | null;
  placeOutlineColorFormula?: string | null;
  placeOutlineSizeFormula?: string | null;
  placeAccessibleOutlineColorFormula?: string | null;
  placeInaccessibleOutlineColorFormula?: string | null;
  placeTextAlignmentFormula?: string | null;
  placeTextPositionFormula?: string | null;
  placeTextOpacityFormula?: string | null;
  placeTextColorFormula?: string | null;
  placeTextSizeFormula?: string | null;
  placeBackgroundColorFormula?: string | null;
  placeBackgroundPatternFormula?: string | null;
  placeBackgroundOpacityFormula?: string | null;
  packageVisibleFormula?: string | null;
  packageLabelFormula?: string | null;
  packageOutlineColorFormula?: string | null;
  packageOutlineSizeFormula?: string | null;
  packageTextAlignmentFormula?: string | null;
  packageTextPositionFormula?: string | null;
  packageTextOpacityFormula?: string | null;
  packageTextColorFormula?: string | null;
  packageTextSizeFormula?: string | null;
  packageBackgroundColorFormula?: string | null;
  packageBackgroundPatternFormula?: string | null;
  packageBackgroundOpacityFormula?: string | null;
  proximityVisibleFormula?: string | null;
  proximityAreaColorFormula?: string | null;
  proximityConeColorFormula?: string | null;
}

async function show(options: ShowDialog) {
  try {
    loading.value = true;
    problems.value = undefined;
    styleId.value = options.id;

    if (options.id !== undefined) {
      title.value = dialogTranslations.edit.title;
    } else {
      title.value = dialogTranslations.create.title;
    }

    color.value = '4CAF50';
    name.value = options.name ?? '';
    shapeLabelFormula.value = options.shapeLabelFormula ?? '';
    shapeColorFormula.value = options.shapeColorFormula ?? '';
    shapeOutlineColorFormula.value = options.shapeOutlineColorFormula ?? '';
    shapeFontSizeFormula.value = options.shapeFontSizeFormula ?? '';
    shapeOutlineSizeFormula.value = options.shapeOutlineSizeFormula ?? '';
    shapeMessageIconFormula.value = options.shapeMessageIconFormula ?? '';
    vehicleColorFormula.value = options.vehicleColorFormula ?? '';
    placeVisibleFormula.value = options.placeVisibleFormula ?? '';
    placeLabelFormula.value = options.placeLabelFormula ?? '';
    placeOutlineColorFormula.value = options.placeOutlineColorFormula ?? '';
    placeOutlineSizeFormula.value = options.placeOutlineSizeFormula ?? '';
    placeAccessibleOutlineColorFormula.value = options.placeAccessibleOutlineColorFormula ?? '';
    placeInaccessibleOutlineColorFormula.value = options.placeInaccessibleOutlineColorFormula ?? '';
    placeTextAlignmentFormula.value = options.placeTextAlignmentFormula ?? '';
    placeTextPositionFormula.value = options.placeTextPositionFormula ?? '';
    placeTextOpacityFormula.value = options.placeTextOpacityFormula ?? '';
    placeTextColorFormula.value = options.placeTextColorFormula ?? '';
    placeTextSizeFormula.value = options.placeTextSizeFormula ?? '';
    placeBackgroundColorFormula.value = options.placeBackgroundColorFormula ?? '';
    placeBackgroundPatternFormula.value = options.placeBackgroundPatternFormula ?? '';
    placeBackgroundOpacityFormula.value = options.placeBackgroundOpacityFormula ?? '';
    packageVisibleFormula.value = options.packageVisibleFormula ?? '';
    packageLabelFormula.value = options.packageLabelFormula ?? '';
    packageOutlineColorFormula.value = options.packageOutlineColorFormula ?? '';
    packageOutlineSizeFormula.value = options.packageOutlineSizeFormula ?? '';
    packageTextAlignmentFormula.value = options.packageTextAlignmentFormula ?? '';
    packageTextPositionFormula.value = options.packageTextPositionFormula ?? '';
    packageTextOpacityFormula.value = options.packageTextOpacityFormula ?? '';
    packageTextColorFormula.value = options.packageTextColorFormula ?? '';
    packageTextSizeFormula.value = options.packageTextSizeFormula ?? '';
    packageBackgroundColorFormula.value = options.packageBackgroundColorFormula ?? '';
    packageBackgroundPatternFormula.value = options.packageBackgroundPatternFormula ?? '';
    packageBackgroundOpacityFormula.value = options.packageBackgroundOpacityFormula ?? '';
    proximityVisibleFormula.value = options.proximityVisibleFormula ?? '';
    proximityAreaColorFormula.value = options.proximityAreaColorFormula ?? '';
    proximityConeColorFormula.value = options.proximityConeColorFormula ?? '';
    initialValues.value = options;
    visible.value = true;

  } finally {
    loading.value = false;
  }
}

async function onSave() {

  try {
    loading.value = true;

    const id = styleId.value;

    if (id === undefined) {
      await create();
    } else {
      await update(id);
    }

  } finally {
    loading.value = false;
  }
}

async function create() {
  const request: CreateMapStyleRequest = {
    style: {
      name: name.value,

      shapeColorFormula: shapeColorFormula.value,
      shapeLabelFormula: shapeLabelFormula.value,
      shapeOutlineColorFormula: shapeOutlineColorFormula.value,
      shapeOutLineSizeFormula: shapeOutlineSizeFormula.value,
      shapeFontSizeFormula: shapeFontSizeFormula.value,
      shapeMessageIconFormula: shapeMessageIconFormula.value,

      vehicleColorFormula: vehicleColorFormula.value,

      placeVisibleFormula: placeVisibleFormula.value,
      placeLabelFormula: placeLabelFormula.value,
      placeOutlineColorFormula: placeOutlineColorFormula.value,
      placeOutlineSizeFormula: placeOutlineSizeFormula.value,
      placeAccessibleOutlineColorFormula: placeAccessibleOutlineColorFormula.value,
      placeInaccessibleOutlineColorFormula: placeInaccessibleOutlineColorFormula.value,
      placeTextAlignmentFormula: placeTextAlignmentFormula.value,
      placeTextPositionFormula: placeTextPositionFormula.value,
      placeTextOpacityFormula: placeTextOpacityFormula.value,
      placeTextColorFormula: placeTextColorFormula.value,
      placeTextSizeFormula: placeTextSizeFormula.value,
      placeBackgroundColorFormula: placeBackgroundColorFormula.value,
      placeBackgroundPatternFormula: placeBackgroundPatternFormula.value,
      placeBackgroundOpacityFormula: placeBackgroundOpacityFormula.value,
      packageVisibleFormula: packageVisibleFormula.value,
      packageLabelFormula: packageLabelFormula.value,
      packageOutlineColorFormula: packageOutlineColorFormula.value,
      packageOutlineSizeFormula: packageOutlineSizeFormula.value,
      packageTextAlignmentFormula: packageTextAlignmentFormula.value,
      packageTextPositionFormula: packageTextPositionFormula.value,
      packageTextOpacityFormula: packageTextOpacityFormula.value,
      packageTextColorFormula: packageTextColorFormula.value,
      packageTextSizeFormula: packageTextSizeFormula.value,
      packageBackgroundColorFormula: packageBackgroundColorFormula.value,
      packageBackgroundPatternFormula: packageBackgroundPatternFormula.value,
      packageBackgroundOpacityFormula: packageBackgroundOpacityFormula.value,
      proximityVisibleFormula: proximityVisibleFormula.value,
      proximityAreaColorFormula: proximityAreaColorFormula.value,
      proximityConeColorFormula: proximityConeColorFormula.value,
    }
  };

  const response = await createMapStyleApi.execute(request);

  if (!response.ok) {
    problems.value = response.details;
    return;
  }

  visible.value = false;
}

async function update(id: string) {

  const request: UpdateMapStyleRequest = {
    styleId: id,
    style: {
      name: name.value,
      shapeColorFormula: shapeColorFormulaChanged.value ? shapeColorFormula.value : undefined,
      shapeLabelFormula: shapeLabelFormulaChanged.value ? shapeLabelFormula.value : undefined,
      shapeOutlineColorFormula: shapeOutlineColorFormulaChanged.value ? shapeOutlineColorFormula.value : undefined,
      shapeOutlineSizeFormula: shapeOutlineSizeFormulaChanged.value ? shapeOutlineSizeFormula.value : undefined,
      vehicleColorFormula: vehicleColorFormulaChanged.value ? vehicleColorFormula.value : undefined,
      shapeFontSizeFormula: shapeFontSizeFormula.value ? shapeFontSizeFormula.value : undefined,
      shapeMessageIconFormula: shapeMessageIconFormula.value ? shapeMessageIconFormula.value : undefined,
      placeVisibleFormula: placeVisibleFormulaChanged.value ? placeVisibleFormula.value : undefined,
      placeLabelFormula: placeLabelFormulaChanged.value ? placeLabelFormula.value : undefined,
      placeOutlineColorFormula: placeOutlineColorFormulaChanged.value ? placeOutlineColorFormula.value : undefined,
      placeOutlineSizeFormula: placeOutlineSizeFormulaChanged.value ? placeOutlineSizeFormula.value : undefined,
      placeAccessibleOutlineColorFormula: placeAccessibleOutlineColorFormulaChanged.value ? placeAccessibleOutlineColorFormula.value : undefined,
      placeInaccessibleOutlineColorFormula: placeInaccessibleOutlineColorFormulaChanged.value ? placeInaccessibleOutlineColorFormula.value : undefined,
      placeTextAlignmentFormula: placeTextAlignmentFormulaChanged.value ? placeTextAlignmentFormula.value : undefined,
      placeTextPositionFormula: placeTextPositionFormulaChanged.value ? placeTextPositionFormula.value : undefined,
      placeTextOpacityFormula: placeTextOpacityFormulaChanged.value ? placeTextOpacityFormula.value : undefined,
      placeTextColorFormula: placeTextColorFormulaChanged.value ? placeTextColorFormula.value : undefined,
      placeTextSizeFormula: placeTextSizeFormulaChanged.value ? placeTextSizeFormula.value : undefined,
      placeBackgroundColorFormula: placeBackgroundColorFormulaChanged.value ? placeBackgroundColorFormula.value : undefined,
      placeBackgroundPatternFormula: placeBackgroundPatternFormulaChanged.value ? placeBackgroundPatternFormula.value : undefined,
      placeBackgroundOpacityFormula: placeBackgroundOpacityFormulaChanged.value ? placeBackgroundOpacityFormula.value : undefined,
      packageVisibleFormula: packageVisibleFormulaChanged.value ? packageVisibleFormula.value : undefined,
      packageLabelFormula: packageLabelFormulaChanged.value ? packageLabelFormula.value : undefined,
      packageOutlineColorFormula: packageOutlineColorFormulaChanged.value ? packageOutlineColorFormula.value : undefined,
      packageOutlineSizeFormula: packageOutlineSizeFormulaChanged.value ? packageOutlineSizeFormula.value : undefined,
      packageTextAlignmentFormula: packageTextAlignmentFormulaChanged.value ? packageTextAlignmentFormula.value : undefined,
      packageTextPositionFormula: packageTextPositionFormulaChanged.value ? packageTextPositionFormula.value : undefined,
      packageTextOpacityFormula: packageTextOpacityFormulaChanged.value ? packageTextOpacityFormula.value : undefined,
      packageTextColorFormula: packageTextColorFormulaChanged.value ? packageTextColorFormula.value : undefined,
      packageTextSizeFormula: packageTextSizeFormulaChanged.value ? packageTextSizeFormula.value : undefined,
      packageBackgroundColorFormula: packageBackgroundColorFormulaChanged.value ? packageBackgroundColorFormula.value : undefined,
      packageBackgroundPatternFormula: packageBackgroundPatternFormulaChanged.value ? packageBackgroundPatternFormula.value : undefined,
      packageBackgroundOpacityFormula: packageBackgroundOpacityFormulaChanged.value ? packageBackgroundOpacityFormula.value : undefined,
      proximityVisibleFormula: proximityVisibleFormulaChanged.value ? proximityVisibleFormula.value : undefined,
      proximityAreaColorFormula: proximityAreaColorFormulaChanged.value ? proximityAreaColorFormula.value : undefined,
      proximityConeColorFormula: proximityConeColorFormulaChanged.value ? proximityConeColorFormula.value : undefined
    }
  };

  const response = await updateMapStyle.execute(request);

  if (!response.ok) {
    problems.value = response.details;
    return;
  }

  styleId.value = undefined;
  visible.value = false;
}

function onHide() {
  styleId.value = undefined;
}

function compilationMessage(compilation?: CompilationResult) {
  if (compilation?.success) return;
  return compilation?.message;
}

</script>

<template>
  <Dialog class="p-dialog-maximized edit-map-style-dialog" modal ref="dialog" v-model:visible="visible" :header="title"
    @hide="onHide" :closeOnEscape="false">
    <Splitter>
        <SplitterPanel class="form-panel">
          <div class="style-form">
            <ProblemDetailsView v-model="problems"></ProblemDetailsView>
            <Message severity="info">{{ dialogTranslations.tip }}</Message>
            <Message severity="info">
              {{ dialogTranslations.documentation }}

              <Button as="a" :label="dialogTranslations.documentationLinkText"
                href="https://wiki.datapolarna.se/wiki/SILog_map_styles" target="_blank" rel="noopener"
                icon="pi pi-external-link" iconPos="right"></Button>
            </Message>

            <div class="field" :class="{ changed: nameChanged }">
              <label for="edit-map-style-dialog-name">{{ translations.generic.name }}</label>
              <InputText autocomplete="off" v-model:modelValue="name" autofocus id="edit-map-style-dialog-name">
              </InputText>
              <Message v-if="nameInvalid" severity="error" variant="simple">{{ nameInvalid }}</Message>
            </div>

            <div class="field">
              <label for="edit-product-dialog-color">{{ dialogTranslations.colorPicker }}</label>
              <InputGroup>
                <InputGroupAddon>
                  <ColorPicker v-model:model-value="color" format="hex"></ColorPicker>
                </InputGroupAddon>
                <InputText autocomplete="off" id="edit-product-dialog-color" v-model:model-value="color">
                </InputText>
              </InputGroup>
            </div>

            <FieldSet :legend="dialogTranslations.shapeSet">

              <div class="field" :class="{ changed: shapeLabelFormulaChanged }">
                <label for="edit-map-style-dialog-shape-label-formula">{{ dialogTranslations.labelFormula }}</label>
                <FormulaEditor :lang="shapeLanguage" v-model:modelValue="shapeLabelFormula"
                  id="edit-map-style-dialog-shape-label-formula"></FormulaEditor>
                <Message v-if="shapeLabelCompilationMessage" severity="error" variant="simple">{{ shapeLabelCompilationMessage }}</Message>
              </div>

              <div class="field" :class="{ changed: shapeColorFormulaChanged }">
                <label for="edit-map-style-dialog-shape-color-formula">{{ dialogTranslations.colorFormula }}</label>
                <FormulaEditor :lang="shapeLanguage" v-model:modelValue="shapeColorFormula" color-picker
                  id="edit-map-style-dialog-shape-color-formula"></FormulaEditor>
                <Message v-if="shapeColorCompilationMessage" severity="error" variant="simple">{{ shapeColorCompilationMessage }}</Message>
              </div>

              <div class="field" :class="{ changed: shapeOutlineColorFormulaChanged }">
                <label for="edit-map-style-dialog-shape-outline-color-formula">{{ dialogTranslations.outlineColorFormula
                  }}</label>
                <FormulaEditor :lang="shapeLanguage" v-model:modelValue="shapeOutlineColorFormula" color-picker
                  id="edit-map-style-dialog-shape-outline-color-formula"></FormulaEditor>
                <Message v-if="shapeOutlineColorCompilationMessage" severity="error" variant="simple">{{ shapeOutlineColorCompilationMessage }}</Message>
              </div>

              <div class="field" :class="{ changed: shapeOutlineSizeFormulaChanged }">
                <label for="edit-map-style-dialog-shape-outline-size-formula">{{ dialogTranslations.outlineSizeFormula
                  }}</label>
                <FormulaEditor :lang="shapeLanguage" v-model:modelValue="shapeOutlineSizeFormula"
                  id="edit-map-style-dialog-shape-outline-size-formula"></FormulaEditor>
                <Message v-if="shapeOutlineSizeCompilationMessage" severity="error" variant="simple">{{ shapeOutlineSizeCompilationMessage }}</Message>
              </div>

              <div class="field" :class="{ changed: shapeFontSizeFormulaChanged }">
                <label for="edit-map-style-dialog-shape-font-size-formula">{{ dialogTranslations.fontSizeFormula
                  }}</label>
                <FormulaEditor :lang="shapeLanguage" v-model:modelValue="shapeFontSizeFormula"
                  id="edit-map-style-dialog-shape-font-size-formula"></FormulaEditor>
                <Message v-if="shapeFontSizeCompilationMessage" severity="error" variant="simple">{{ shapeFontSizeCompilationMessage }}</Message>
              </div>

              <div class="field" :class="{ changed: shapeMessageIconFormulaChanged }">
                <label for="edit-map-style-dialog-shape-message-icon-formula">{{ dialogTranslations.messageIconFormula
                  }}</label>
                <FormulaEditor :lang="shapeLanguage" v-model:modelValue="shapeMessageIconFormula"
                  id="edit-map-style-dialog-shape-message-icon-formula"></FormulaEditor>
                <Message v-if="shapeMessageIconCompilationMessage" severity="error" variant="simple">{{ shapeMessageIconCompilationMessage }}</Message>
              </div> 
            </FieldSet>

            <FieldSet :legend="dialogTranslations.vehicleSet">
              <div class="field" :class="{ changed: vehicleColorFormulaChanged }">
                <label for="edit-map-style-dialog-vehicle-color-formula">{{ dialogTranslations.colorFormula }}</label>
                <FormulaEditor :lang="vehicleLanguage" v-model:modelValue="vehicleColorFormula" color-picker
                  id="edit-map-style-dialog-vehicle-color-formula"></FormulaEditor>
                <Message v-if="vehicleColorCompilationMessage" severity="error" variant="simple">{{ vehicleColorCompilationMessage }}</Message>
              </div>
            </FieldSet>

            <FieldSet :legend="dialogTranslations.placeSet" toggleable :collapsed="placesSectionCollapsed" @toggle="placesSectionCollapsed = $event.value">

              <div class="field" :class="{ changed: placeVisibleFormulaChanged }">
                <label for="edit-map-style-dialog-place-visible-formula">{{ dialogTranslations.placeVisibleFormula }}</label>
                <FormulaEditor :lang="placeLanguage" v-model:modelValue="placeVisibleFormula"
                  id="edit-map-style-dialog-place-visible-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: placeLabelFormulaChanged }">
                <label for="edit-map-style-dialog-place-label-formula">{{ dialogTranslations.placeLabelFormula }}</label>
                <FormulaEditor :lang="placeLanguage" v-model:modelValue="placeLabelFormula"
                  id="edit-map-style-dialog-place-label-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: placeOutlineColorFormulaChanged }">
                <label for="edit-map-style-dialog-place-outline-color-formula">{{ dialogTranslations.placeOutlineColorFormula }}</label>
                <FormulaEditor :lang="placeLanguage" v-model:modelValue="placeOutlineColorFormula" color-picker
                  id="edit-map-style-dialog-place-outline-color-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: placeOutlineSizeFormulaChanged }">
                <label for="edit-map-style-dialog-place-outline-size-formula">{{ dialogTranslations.placeOutlineSizeFormula }}</label>
                <FormulaEditor :lang="placeLanguage" v-model:modelValue="placeOutlineSizeFormula"
                  id="edit-map-style-dialog-place-outline-size-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: placeAccessibleOutlineColorFormulaChanged }">
                <label for="edit-map-style-dialog-place-accessible-outline-color-formula">{{ dialogTranslations.placeAccessibleOutlineColorFormula }}</label>
                <FormulaEditor :lang="placeLanguage" v-model:modelValue="placeAccessibleOutlineColorFormula" color-picker
                  id="edit-map-style-dialog-place-accessible-outline-color-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: placeInaccessibleOutlineColorFormulaChanged }">
                <label for="edit-map-style-dialog-place-inaccessible-outline-color-formula">{{ dialogTranslations.placeInaccessibleOutlineColorFormula }}</label>
                <FormulaEditor :lang="placeLanguage" v-model:modelValue="placeInaccessibleOutlineColorFormula" color-picker
                  id="edit-map-style-dialog-place-inaccessible-outline-color-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: placeTextColorFormulaChanged }">
                <label for="edit-map-style-dialog-place-text-color-formula">{{ dialogTranslations.placeTextColorFormula }}</label>
                <FormulaEditor :lang="placeLanguage" v-model:modelValue="placeTextColorFormula" color-picker
                  id="edit-map-style-dialog-place-text-color-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: placeTextSizeFormulaChanged }">
                <label for="edit-map-style-dialog-place-text-size-formula">{{ dialogTranslations.placeTextSizeFormula }}</label>
                <FormulaEditor :lang="placeLanguage" v-model:modelValue="placeTextSizeFormula"
                  id="edit-map-style-dialog-place-text-size-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: placeTextAlignmentFormulaChanged }">
                <label for="edit-map-style-dialog-place-text-alignment-formula">{{ dialogTranslations.placeTextAlignmentFormula }}</label>
                <FormulaEditor :lang="placeLanguage" v-model:modelValue="placeTextAlignmentFormula"
                  id="edit-map-style-dialog-place-text-alignment-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: placeTextPositionFormulaChanged }">
                <label for="edit-map-style-dialog-place-text-position-formula">{{ dialogTranslations.placeTextPositionFormula }}</label>
                <FormulaEditor :lang="placeLanguage" v-model:modelValue="placeTextPositionFormula"
                  id="edit-map-style-dialog-place-text-position-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: placeTextOpacityFormulaChanged }">
                <label for="edit-map-style-dialog-place-text-opacity-formula">{{ dialogTranslations.placeTextOpacityFormula }}</label>
                <FormulaEditor :lang="placeLanguage" v-model:modelValue="placeTextOpacityFormula"
                  id="edit-map-style-dialog-place-text-opacity-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: placeBackgroundColorFormulaChanged }">
                <label for="edit-map-style-dialog-place-background-color-formula">{{ dialogTranslations.placeBackgroundColorFormula }}</label>
                <FormulaEditor :lang="placeLanguage" v-model:modelValue="placeBackgroundColorFormula" color-picker
                  id="edit-map-style-dialog-place-background-color-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: placeBackgroundPatternFormulaChanged }">
                <label for="edit-map-style-dialog-place-background-pattern-formula">{{ dialogTranslations.placeBackgroundPatternFormula }}</label>
                <FormulaEditor :lang="placeLanguage" v-model:modelValue="placeBackgroundPatternFormula"
                  id="edit-map-style-dialog-place-background-pattern-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: placeBackgroundOpacityFormulaChanged }">
                <label for="edit-map-style-dialog-place-background-opacity-formula">{{ dialogTranslations.placeBackgroundOpacityFormula }}</label>
                <FormulaEditor :lang="placeLanguage" v-model:modelValue="placeBackgroundOpacityFormula"
                  id="edit-map-style-dialog-place-background-opacity-formula"></FormulaEditor>
              </div>

            </FieldSet>

            <FieldSet :legend="dialogTranslations.packageSet">
              <div class="field" :class="{ changed: packageVisibleFormulaChanged }">
                <label for="edit-map-style-dialog-package-visible-formula">{{ dialogTranslations.packageVisibleFormula }}</label>
                <FormulaEditor :lang="packageLanguage" v-model:modelValue="packageVisibleFormula"
                  id="edit-map-style-dialog-package-visible-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: packageLabelFormulaChanged }">
                <label for="edit-map-style-dialog-package-label-formula">{{ dialogTranslations.packageLabelFormula }}</label>
                <FormulaEditor :lang="packageLanguage" v-model:modelValue="packageLabelFormula"
                  id="edit-map-style-dialog-package-label-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: packageOutlineColorFormulaChanged }">
                <label for="edit-map-style-dialog-package-outline-color-formula">{{ dialogTranslations.packageOutlineColorFormula }}</label>
                <FormulaEditor :lang="packageLanguage" v-model:modelValue="packageOutlineColorFormula" color-picker
                  id="edit-map-style-dialog-package-outline-color-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: packageOutlineSizeFormulaChanged }">
                <label for="edit-map-style-dialog-package-outline-size-formula">{{ dialogTranslations.packageOutlineSizeFormula }}</label>
                <FormulaEditor :lang="packageLanguage" v-model:modelValue="packageOutlineSizeFormula"
                  id="edit-map-style-dialog-package-outline-size-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: packageTextColorFormulaChanged }">
                <label for="edit-map-style-dialog-package-text-color-formula">{{ dialogTranslations.packageTextColorFormula }}</label>
                <FormulaEditor :lang="packageLanguage" v-model:modelValue="packageTextColorFormula" color-picker
                  id="edit-map-style-dialog-package-text-color-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: packageTextSizeFormulaChanged }">
                <label for="edit-map-style-dialog-package-text-size-formula">{{ dialogTranslations.packageTextSizeFormula }}</label>
                <FormulaEditor :lang="packageLanguage" v-model:modelValue="packageTextSizeFormula"
                  id="edit-map-style-dialog-package-text-size-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: packageTextAlignmentFormulaChanged }">
                <label for="edit-map-style-dialog-package-text-alignment-formula">{{ dialogTranslations.packageTextAlignmentFormula }}</label>
                <FormulaEditor :lang="packageLanguage" v-model:modelValue="packageTextAlignmentFormula"
                  id="edit-map-style-dialog-package-text-alignment-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: packageTextPositionFormulaChanged }">
                <label for="edit-map-style-dialog-package-text-position-formula">{{ dialogTranslations.packageTextPositionFormula }}</label>
                <FormulaEditor :lang="packageLanguage" v-model:modelValue="packageTextPositionFormula"
                  id="edit-map-style-dialog-package-text-position-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: packageTextOpacityFormulaChanged }">
                <label for="edit-map-style-dialog-package-text-opacity-formula">{{ dialogTranslations.packageTextOpacityFormula }}</label>
                <FormulaEditor :lang="packageLanguage" v-model:modelValue="packageTextOpacityFormula"
                  id="edit-map-style-dialog-package-text-opacity-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: packageBackgroundColorFormulaChanged }">
                <label for="edit-map-style-dialog-package-background-color-formula">{{ dialogTranslations.packageBackgroundColorFormula }}</label>
                <FormulaEditor :lang="packageLanguage" v-model:modelValue="packageBackgroundColorFormula" color-picker
                  id="edit-map-style-dialog-package-background-color-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: packageBackgroundPatternFormulaChanged }">
                <label for="edit-map-style-dialog-package-background-pattern-formula">{{ dialogTranslations.packageBackgroundPatternFormula }}</label>
                <FormulaEditor :lang="packageLanguage" v-model:modelValue="packageBackgroundPatternFormula"
                  id="edit-map-style-dialog-package-background-pattern-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: packageBackgroundOpacityFormulaChanged }">
                <label for="edit-map-style-dialog-package-background-opacity-formula">{{ dialogTranslations.packageBackgroundOpacityFormula }}</label>
                <FormulaEditor :lang="packageLanguage" v-model:modelValue="packageBackgroundOpacityFormula"
                  id="edit-map-style-dialog-package-background-opacity-formula"></FormulaEditor>
              </div>
            </FieldSet>

            <FieldSet :legend="dialogTranslations.proximitySet">
              <div class="field" :class="{ changed: proximityVisibleFormulaChanged }">
                <label for="edit-map-style-dialog-proximity-visible-formula">{{ dialogTranslations.proximityVisibleFormula }}</label>
                <FormulaEditor :lang="proximityLanguage" v-model:modelValue="proximityVisibleFormula"
                  id="edit-map-style-dialog-proximity-visible-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: proximityAreaColorFormulaChanged }">
                <label for="edit-map-style-dialog-proximity-area-color-formula">{{ dialogTranslations.proximityAreaColorFormula }}</label>
                <FormulaEditor :lang="proximityLanguage" v-model:modelValue="proximityAreaColorFormula" color-picker
                  id="edit-map-style-dialog-proximity-area-color-formula"></FormulaEditor>
              </div>

              <div class="field" :class="{ changed: proximityConeColorFormulaChanged }">
                <label for="edit-map-style-dialog-proximity-cone-color-formula">{{ dialogTranslations.proximityConeColorFormula }}</label>
                <FormulaEditor :lang="proximityLanguage" v-model:modelValue="proximityConeColorFormula" color-picker
                  id="edit-map-style-dialog-proximity-cone-color-formula"></FormulaEditor>
              </div>
            </FieldSet>

            <div class="actions">
              <Button class="submit-button" :label="translations.generic.save" @click="onSave" :loading="loading"></Button>
            </div>
          </div>
        </SplitterPanel>
        <SplitterPanel>
          <PreviewMapView :map-style="previewMapStyle.mapStyle" class="preview-map"></PreviewMapView>
        </SplitterPanel>
      </Splitter>
  </Dialog>
</template>

<style scoped>
.style-form {
  padding-block: .5rem;
  padding-inline: .5rem;
  display: grid;
  row-gap: .5rem;
  column-gap: .5rem;
  grid-template-columns: 1fr;
  min-width: 30rem;
}

.description-field {
  grid-column: 1/4;
}

.field {
  display: flex;
  flex-direction: column;
}

.submit-button {
  justify-self: end;
}

.log-fields {
  grid-column: 1/4;
}

.log-fields :deep(.p-fieldset-content) {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: .5rem;
  column-gap: .5rem;
}

.field :deep(.p-inputnumber-input) {
  text-align: right;
}

.toggle-field {
  display: flex;
  align-items: center;
  column-gap: .5rem;
}

.actions {
  display: flex;
  flex-direction: row;
  justify-content: end;
  gap: .5rem;
}

.preview-map {
  max-height: 100%;
}

.form-panel {
  overflow-y: auto;
}

</style>

<style>
.edit-map-style-dialog .p-dialog-content {
  overflow-x: hidden;
  overflow-y: hidden;
}

.edit-map-style-dialog .p-fieldset {
  display: grid;
}

.edit-map-style-dialog .p-fieldset-content-container {
  overflow-x: hidden;
}

.edit-map-style-dialog .p-splitter {
  min-height: 100%;
  max-height: 100%;

}
</style>
